import React, { useMemo } from "react";
import { CircularProgress, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { buildPlayerPagePath } from "../../pages/Player/playerTabs";
import "./HomeDraftOddsBoard.css";

const DEFAULT_PLAYER_IMAGE =
    "https://awoiaf.westeros.org/images/thumb/5/56/No_portrait.svg/240px-No_portrait.svg.png";

function formatName(player) {
    return `${player.first_name || ""} ${player.last_name || ""}`.trim();
}

function DistributionStrip({ distribution, depth = 32 }) {
    const cells = useMemo(() => {
        const byPick = new Map((distribution || []).map((d) => [d.pick, d.odds]));
        const maxOdds = Math.max(...(distribution || []).map((d) => d.odds), 0.001);
        return Array.from({ length: depth }, (_, index) => {
            const pick = index + 1;
            const odds = byPick.get(pick) || 0;
            return {
                pick,
                odds,
                intensity: odds / maxOdds,
            };
        });
    }, [distribution, depth]);

    return (
        <div className="home-draft-odds-strip" aria-hidden="true">
            {cells.map((cell) => (
                <div
                    key={cell.pick}
                    className="home-draft-odds-strip-cell"
                    style={{ "--intensity": cell.intensity }}
                    title={`Pick ${cell.pick}: ${(cell.odds * 100).toFixed(1)}%`}
                />
            ))}
        </div>
    );
}

function PickSlotCard({ slot }) {
    const leader = slot.candidates[0];

    return (
        <article className="home-draft-odds-pick-card">
            <div className="home-draft-odds-pick-header">
                <div className="home-draft-odds-pick-badge">
                    <span className="home-draft-odds-pick-num">#{slot.pick}</span>
                    <span className="home-draft-odds-pick-round">R{slot.round}</span>
                </div>
                {slot.team_logo && (
                    <img
                        src={slot.team_logo}
                        alt=""
                        className="home-draft-odds-team-logo"
                        loading="lazy"
                    />
                )}
                <span className="home-draft-odds-team-name">{slot.team_name}</span>
            </div>

            {slot.candidates.length === 0 ? (
                <p className="home-draft-odds-empty-pick">No odds yet</p>
            ) : (
                <div className="home-draft-odds-candidates">
                    {slot.candidates.map((candidate, index) => (
                        <Link
                            key={candidate.player_id}
                            to={buildPlayerPagePath(candidate.player_id, "draft-odds")}
                            className={`home-draft-odds-candidate${index === 0 ? " home-draft-odds-candidate--leader" : ""}`}
                        >
                            <div className="home-draft-odds-candidate-top">
                                <span className="home-draft-odds-candidate-name">
                                    {formatName(candidate)}
                                </span>
                                <span className="home-draft-odds-candidate-pct">
                                    {(candidate.odds * 100).toFixed(0)}%
                                </span>
                            </div>
                            <div className="home-draft-odds-candidate-bar">
                                <div
                                    className="home-draft-odds-candidate-bar-fill"
                                    style={{ width: `${Math.min(candidate.odds * 100, 100)}%` }}
                                />
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {leader && (
                <div className="home-draft-odds-pick-footnote">
                    Most likely: {formatName(leader)}
                </div>
            )}
        </article>
    );
}

function ProspectCard({ prospect }) {
    return (
        <Link
            to={buildPlayerPagePath(prospect.player_id, "draft-odds")}
            className="home-draft-odds-prospect-card"
        >
            <div className="home-draft-odds-prospect-head">
                <span className="home-draft-odds-prospect-rank">#{prospect.consensus_ranking}</span>
                <div className="home-draft-odds-prospect-identity">
                    <img
                        src={prospect.player_image_url || DEFAULT_PLAYER_IMAGE}
                        alt=""
                        className="home-draft-odds-prospect-avatar"
                        loading="lazy"
                    />
                    <div className="home-draft-odds-prospect-text">
                        <span className="home-draft-odds-prospect-name">{formatName(prospect)}</span>
                        <span className="home-draft-odds-prospect-meta">
                            {prospect.position}
                            {prospect.flag_url && (
                                <img src={prospect.flag_url} alt="" className="home-draft-odds-flag" loading="lazy" />
                            )}
                        </span>
                    </div>
                </div>
                <div className="home-draft-odds-prospect-exp">
                    <span className="home-draft-odds-prospect-exp-label">Exp.</span>
                    <span className="home-draft-odds-prospect-exp-value">{prospect.expected_pick.toFixed(1)}</span>
                </div>
            </div>

            <DistributionStrip distribution={prospect.distribution} />

            <div className="home-draft-odds-prospect-stats">
                <span>{(prospect.p_first * 100).toFixed(0)}% at #1</span>
                <span>{(prospect.p_top5 * 100).toFixed(0)}% top 5</span>
                <span>Modal #{prospect.modal_pick}</span>
            </div>
        </Link>
    );
}

function HomeDraftOddsBoard({ loading, payload, draftYear }) {
    const hasPicks = payload?.picks?.some((slot) => slot.candidates?.length > 0);
    const hasProspects = payload?.prospects?.length > 0;

    if (loading) {
        return (
            <section className="home-draft-odds-section">
                <div className="home-draft-odds-loading">
                    <CircularProgress size={28} sx={{ color: "var(--brand-strong)" }} />
                </div>
            </section>
        );
    }

    if (!hasPicks && !hasProspects) {
        return null;
    }

    return (
        <section className="home-draft-odds-section">
            <div className="home-draft-odds-header">
                <div>
                    <Typography variant="h4" component="h2" className="home-draft-odds-title">
                        Draft Odds Board
                    </Typography>
                    <Typography variant="body2" component="p" className="home-draft-odds-subtitle">
                        Simulated pick probabilities for the {draftYear} NHL Draft — who goes where, and how the top prospects could land.
                    </Typography>
                </div>
            </div>

            {hasPicks && (
                <div className="home-draft-odds-picks-panel">
                    <Typography variant="overline" className="home-draft-odds-panel-label">
                        Likely names by pick
                    </Typography>
                    <div className="home-draft-odds-picks-scroll">
                        {payload.picks.map((slot) => (
                            <PickSlotCard key={slot.pick} slot={slot} />
                        ))}
                    </div>
                </div>
            )}

            {hasProspects && (
                <div className="home-draft-odds-prospects-panel">
                    <Typography variant="overline" className="home-draft-odds-panel-label">
                        Top prospect landing spots
                    </Typography>
                    <div className="home-draft-odds-prospects-grid">
                        {payload.prospects.map((prospect) => (
                            <ProspectCard key={prospect.player_id} prospect={prospect} />
                        ))}
                    </div>
                </div>
            )}

            {hasProspects && (
                <div className="home-draft-odds-footer">
                    <Link
                        to={buildPlayerPagePath(payload.prospects[0].player_id, "draft-odds")}
                        className="view-more-link"
                    >
                        <Typography variant="body1">
                            Explore full draft odds
                            <ArrowForwardIosIcon />
                        </Typography>
                    </Link>
                </div>
            )}
        </section>
    );
}

export default HomeDraftOddsBoard;
